// Lógica pura (sin React/Firebase) para editar el detalle de una orden de
// compra ya guardada (OrderDetail.jsx). No calcula pagos ni escribe en
// Firebase — eso lo hace el componente, usando actualizarOrdenDeRegistro
// (registry-actions.js) + changesRegistryFunc (firebase/firebasejs.jsx).

// Algunas órdenes viejas se guardaron como objeto keyed por `code` (no
// soporta 2 ítems con el mismo code, ej. 2 playeras de tallas distintas);
// las nuevas se arman como array (ver GroupRegistrySummary.jsx). Siempre se
// trabaja en memoria como array — al guardar se escribe siempre como array,
// estandariza hacia adelante sin romper la lectura de órdenes viejas.
export function normalizarOrder(order) {
  if (!order) return [];
  if (Array.isArray(order)) return order;
  return Object.values(order);
}

export function calcularTotal(items) {
  return (items || []).reduce((acc, item) => {
    const cantidad = Number(item.quantity) || 0;
    const precio = Number(item.unit_price) || 0;
    return acc + cantidad * precio;
  }, 0);
}

// Ítem de ajuste que se agrega cuando, tras editar, el total ya no coincide
// con el que había antes de esa sesión de edición — su precio es
// exactamente la diferencia, para que la orden vuelva a sumar lo mismo que
// antes (y así, sesión tras sesión, el total pagado original queda
// preservado indefinidamente). Redondeado a 2 decimales para evitar basura
// de punto flotante (ej. 0.1 + 0.2).
export function construirItemAjuste(diferencia, nota) {
  return {
    code: "ajuste_" + Date.now(),
    name: "Ajuste de orden",
    description: nota,
    quantity: 1,
    unit_price: Math.round(diferencia * 100) / 100,
    precioKey: "",
  };
}

// Texto legible para la bitácora (changesRegistry) a partir de los ítems
// antes/después de una sesión de edición. Compara por `_uid` (asignado por
// el componente al entrar a modo edición, no se persiste) en vez de por
// posición en el array, para no confundirse si se quita un ítem que no es
// el último (lo que correría los índices de todos los siguientes).
//
// `opciones.resolverEtiqueta(item, idCampo)` y
// `opciones.resolverValor(item, idCampo, valorCrudo)` son opcionales — esta
// función no sabe nada de productos/formularios (eso vive en
// eventSubEventsRegistry, fuera del alcance de un archivo puro), así que el
// que llama puede pasar resolvers para mostrar la etiqueta bonita del campo
// (ej. "Talla de playera" en vez de "talladeplayera") y el texto de la
// opción elegida (ej. "Mediana" en vez de "M"). Sin resolvers, se muestra
// el dato crudo tal cual — sigue siendo más preciso que el mensaje genérico
// anterior ("cambió campos de X"), que no decía ni qué campo ni los valores.
export function construirDescripcionCambios(itemsAntes, itemsDespues, opciones) {
  const resolverEtiqueta = (opciones && opciones.resolverEtiqueta) || ((item, idCampo) => idCampo);
  const resolverValor = (opciones && opciones.resolverValor) || ((item, idCampo, valor) => valor);

  const partes = [];
  const despuesPorId = new Map((itemsDespues || []).map((it) => [it._uid, it]));
  const antesPorId = new Map((itemsAntes || []).map((it) => [it._uid, it]));

  (itemsAntes || []).forEach((antes) => {
    if (!despuesPorId.has(antes._uid)) {
      partes.push(`quitó "${antes.name}"`);
    }
  });

  (itemsDespues || []).forEach((despues) => {
    const antes = antesPorId.get(despues._uid);
    if (!antes) {
      partes.push(`agregó "${despues.name}" (cant. ${despues.quantity}, $${despues.unit_price})`);
      return;
    }
    if (antes.quantity !== despues.quantity) {
      partes.push(`cambió cantidad de "${despues.name}" de ${antes.quantity} a ${despues.quantity}`);
    }
    if (antes.unit_price !== despues.unit_price) {
      partes.push(`cambió precio de "${despues.name}" de $${antes.unit_price} a $${despues.unit_price}`);
    }

    const antesData = antes.extraData || {};
    const despuesData = despues.extraData || {};
    const claves = new Set([...Object.keys(antesData), ...Object.keys(despuesData)]);
    claves.forEach((idCampo) => {
      if (antesData[idCampo] === despuesData[idCampo]) return;
      const etiqueta = resolverEtiqueta(despues, idCampo);
      const valorAntes =
        antesData[idCampo] === undefined ? "(sin valor)" : resolverValor(despues, idCampo, antesData[idCampo]);
      const valorDespues =
        despuesData[idCampo] === undefined
          ? "(sin valor)"
          : resolverValor(despues, idCampo, despuesData[idCampo]);
      partes.push(`cambió "${etiqueta}" de "${valorAntes}" a "${valorDespues}" en "${despues.name}"`);
    });
  });

  if (!partes.length) return "Sin cambios";
  return "Editó la orden: " + partes.join("; ") + ".";
}
